import type {
  CatalogRecord,
  DirectoryNode,
  OperationRecord,
  OperationResult,
  OverviewData,
  PendingRecord,
  RecordDetail,
  SnapshotBundle,
  SnapshotMeta,
  SnapshotPointer,
} from '../../shared/types';

export interface HealthState {
  base: { connected: boolean; reason: string };
  drive: { connected: boolean; reason: string };
  write: { enabled: boolean; reason: string };
  snapshot?: SnapshotPointer;
}

interface SnapshotResource<T> {
  value: T;
  etag: string;
  version: string;
}

interface MetaResult {
  notModified: boolean;
  meta: SnapshotMeta | null;
  etag: string | null;
}

class SnapshotVersionFallback extends Error {
  constructor(public version: string) {
    super(`快照资源已回退到 ${version}`);
  }
}

async function request<T>(path: string, init?: RequestInit, bearer?: string): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
      ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as { error?: string };
  if (!response.ok && response.status !== 207) throw new Error(body.error ?? `请求失败（${response.status}）`);
  return body as T;
}

function versionQuery(version: string): string {
  return new URLSearchParams({ version }).toString();
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function normalizeEtag(value: string | null): string | null {
  return value?.trim().replace(/^W\//, '').replace(/^"|"$/g, '') ?? null;
}

async function snapshotResource<T>(
  path: string,
  expectedVersion: string,
  expectedEtag: string,
  signal?: AbortSignal,
): Promise<SnapshotResource<T>> {
  const response = await fetch(`${path}${path.includes('?') ? '&' : '?'}${versionQuery(expectedVersion)}`, { signal });
  const text = await response.text();
  if (!response.ok) {
    let message = `请求失败（${response.status}）`;
    try { message = (JSON.parse(text) as { error?: string }).error ?? message; } catch { /* Keep HTTP fallback. */ }
    throw new Error(message);
  }
  const version = response.headers.get('x-snapshot-version') ?? expectedVersion;
  if (version !== expectedVersion) throw new SnapshotVersionFallback(version);
  const digest = await sha256(text);
  if (digest !== expectedEtag) throw new Error(`快照资源完整性校验失败：${path}`);
  return {
    value: JSON.parse(text) as T,
    etag: digest,
    version,
  };
}

async function downloadSnapshotVersion(
  meta: SnapshotMeta,
  signal?: AbortSignal,
  onCoreReady?: (summary: OverviewData, directory: DirectoryNode) => void,
): Promise<SnapshotBundle> {
  const [summaryResult, directoryResult] = await Promise.all([
    snapshotResource<OverviewData>('/api/snapshot/summary', meta.version, meta.summaryEtag, signal),
    snapshotResource<DirectoryNode>('/api/snapshot/directory', meta.version, meta.directoryEtag, signal),
  ]);
  onCoreReady?.(summaryResult.value, directoryResult.value);

  const [operationsResult, catalogResults, pendingResults] = await Promise.all([
    snapshotResource<OperationRecord[]>('/api/snapshot/operations', meta.version, meta.operationsEtag, signal),
    Promise.all(meta.catalogChunks.map((chunk) => snapshotResource<CatalogRecord[]>(
      `/api/snapshot/catalog/${chunk.index}`,
      meta.version,
      chunk.etag,
      signal,
    ))),
    Promise.all(meta.pendingChunks.map((chunk) => snapshotResource<PendingRecord[]>(
      `/api/snapshot/pending/${chunk.index}`,
      meta.version,
      chunk.etag,
      signal,
    ))),
  ]);

  const catalog = catalogResults.flatMap((result) => result.value);
  const pending = pendingResults.flatMap((result) => result.value);
  if (catalog.length !== meta.recordCount) throw new Error(`catalog 数量校验失败：期望 ${meta.recordCount}，实际 ${catalog.length}`);
  if (pending.length !== meta.pendingCount) throw new Error(`pending 数量校验失败：期望 ${meta.pendingCount}，实际 ${pending.length}`);

  return {
    version: meta.version,
    meta,
    summary: summaryResult.value,
    directory: directoryResult.value,
    catalog,
    pending,
    operations: operationsResult.value,
  };
}

async function fetchSnapshotMeta(etag?: string | null, signal?: AbortSignal, version?: string): Promise<MetaResult> {
  const query = version ? `?${versionQuery(version)}` : '';
  const response = await fetch(`/api/snapshot/meta${query}`, {
    signal,
    headers: etag ? { 'if-none-match': `"${etag}"` } : undefined,
  });
  if (response.status === 304) return { notModified: true, meta: null, etag: etag ?? null };
  const body = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as SnapshotMeta & { error?: string };
  if (!response.ok) throw new Error(body.error ?? `请求失败（${response.status}）`);
  return {
    notModified: false,
    meta: body,
    etag: normalizeEtag(response.headers.get('etag')) ?? body.etag,
  };
}

export async function downloadSnapshot(
  meta: SnapshotMeta,
  signal?: AbortSignal,
  onCoreReady?: (summary: OverviewData, directory: DirectoryNode) => void,
): Promise<SnapshotBundle> {
  try {
    return await downloadSnapshotVersion(meta, signal, onCoreReady);
  } catch (error) {
    if (signal?.aborted) throw error;
    const fallbackVersion = error instanceof SnapshotVersionFallback ? error.version : meta.previousVersion;
    if (!fallbackVersion || fallbackVersion === meta.version) throw error;
    const fallback = await fetchSnapshotMeta(null, signal, fallbackVersion);
    if (!fallback.meta) throw error;
    return downloadSnapshotVersion(fallback.meta, signal, onCoreReady);
  }
}

export const api = {
  health: (signal?: AbortSignal) => request<HealthState>('/api/health', { signal }),
  snapshotMeta: (etag?: string | null, signal?: AbortSignal) => fetchSnapshotMeta(etag, signal),
  detail: async (version: string, recordId: string, signal?: AbortSignal): Promise<RecordDetail> => {
    const response = await fetch(`/api/snapshot/details/${encodeURIComponent(recordId)}?${versionQuery(version)}`, { signal });
    const text = await response.text();
    let body: RecordDetail & { error?: string };
    try { body = JSON.parse(text) as RecordDetail & { error?: string }; }
    catch { throw new Error(`详情读取失败（${response.status}）`); }
    if (!response.ok) throw new Error(body.error ?? `详情读取失败（${response.status}）`);
    const actualVersion = response.headers.get('x-snapshot-version');
    if (!actualVersion || actualVersion !== version) throw new Error('详情分片尚未完成版本传播，请稍后重试');
    const expectedEtag = normalizeEtag(response.headers.get('etag'));
    if (!expectedEtag || await sha256(text) !== expectedEtag) throw new Error('详情资源完整性校验失败');
    return body;
  },
  login: (password: string) => request<{ ok: true; expiresIn: number }>('/api/admin/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),
  session: (signal?: AbortSignal) => request<{ loggedIn: boolean; reason?: string }>('/api/admin/session', { signal }),
  rebuildSnapshot: (refreshSecret?: string) => request<{ ok: true; meta: SnapshotMeta }>(
    '/api/admin/rebuild-snapshot',
    { method: 'POST' },
    refreshSecret,
  ),
  transact: (recordId: string, body: unknown) => request<OperationResult>(`/api/records/${recordId}/transaction`, { method: 'POST', body: JSON.stringify(body) }),
  batchDecision: (operations: unknown[]) => request<{ success: number; failed: number; partial: boolean; results: OperationResult[] }>('/api/batch/decision', { method: 'POST', body: JSON.stringify({ operations }) }),
};
